import "./LoginPage.scss";
import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SigninApi } from "../../services/api/auth";
import { getApiErrorData, getApiErrorMessage } from "../../utils/api/getApiErrorMessage";
import { tokenStorage } from "../../services/auth/tokenStorage";
import { logIn } from "../../store/loggedIn";
import { useDispatch } from "react-redux";
import Header from "../../components/Header/Header";
import YandexLoginButton from "../../components/YandexLoginButton/YandexLoginButton";

type Mode = "login" | "register";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const LoginPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [mode, setMode] = useState<Mode>("login");
  const [login, setLogin] = useState("");
  const [mail, setMail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loginStep, setLoginStep] = useState<"credentials" | "code" | "reset-request" | "reset-password">("credentials");
  const [challengeId, setChallengeId] = useState("");
  const [code, setCode] = useState("");
  const [afterCodePath, setAfterCodePath] = useState("/app");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [personalDataConsent, setPersonalDataConsent] = useState(false);
  const [crossBorderConsent, setCrossBorderConsent] = useState(false);

  const switchMode = (next: Mode) => {
    setMode(next);
    setError(null);
    setLoginStep("credentials");
    setCode("");
  };

  const finishLogin = (accessToken: string, path: string) => {
    tokenStorage.setAccessToken(accessToken);
    dispatch(logIn());
    navigate(path, { replace: true });
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    if (loginStep === "reset-request") {
      if (!EMAIL_PATTERN.test(login.trim())) {
        setError("Укажите корректную почту для восстановления пароля.");
        return;
      }
      try {
        setLoading(true);
        const response = await SigninApi.requestPasswordReset(login.trim());
        setChallengeId(response.challengeId);
        setLoginStep("reset-password");
        setError("Мы отправили код восстановления на вашу почту.");
      } catch (requestError) {
        setError(getApiErrorMessage(requestError, "Не удалось отправить код. Проверьте почту и попробуйте еще раз."));
      } finally {
        setLoading(false);
      }
      return;
    }

    if (loginStep === "reset-password") {
      if (!/^\d{6}$/.test(code)) {
        setError("Введите шестизначный код из письма.");
        return;
      }
      if (password.length < 8 || password.length > 30) {
        setError("Новый пароль должен содержать от 8 до 30 символов.");
        return;
      }
      if (password !== confirmPassword) {
        setError("Пароли не совпадают.");
        return;
      }
      try {
        setLoading(true);
        await SigninApi.confirmPasswordReset(challengeId, code, password);
        setLoginStep("credentials");
        setPassword("");
        setConfirmPassword("");
        setCode("");
        setError("Пароль изменён. Теперь войдите с новым паролем.");
      } catch (requestError) {
        setError(getApiErrorMessage(requestError, "Не удалось изменить пароль. Проверьте код и попробуйте еще раз."));
      } finally {
        setLoading(false);
      }
      return;
    }

    if (loginStep === "code") {
      if (!/^\d{6}$/.test(code)) {
        setError("Введите шестизначный код из письма.");
        return;
      }
      try {
        setLoading(true);
        const response = await SigninApi.verifyLoginCode(challengeId, code);
        finishLogin(response.accessToken, afterCodePath);
      } catch (requestError) {
        setError(getApiErrorMessage(requestError, "Не удалось подтвердить код. Проверьте его и попробуйте еще раз."));
      } finally {
        setLoading(false);
      }
      return;
    }

    if (mode === "register") {
      if (!EMAIL_PATTERN.test(mail.trim())) {
        setError("Укажите корректную почту.");
        return;
      }
      if (password.length < 8 || password.length > 30) {
        setError("Пароль должен содержать от 8 до 30 символов.");
        return;
      }
      if (password !== confirmPassword) {
        setError("Пароли не совпадают.");
        return;
      }
      if (!personalDataConsent) {
        setError("Отметьте согласие на обработку персональных данных.");
        return;
      }
      if (!crossBorderConsent) {
        setError("Отметьте согласие на трансграничную передачу персональных данных.");
        return;
      }

      try {
        setLoading(true);
        await SigninApi.register({ username: undefined, mail: mail.trim(), password, personalDataConsent, crossBorderConsent });

        const response = await SigninApi.login({ username: mail.trim(), password, deviceModel: null, firebaseId: null });
        finishLogin(response.accessToken, "/onboarding");
      } catch (requestError) {
        const data = getApiErrorData(requestError);
        if (typeof data?.challengeId === "string" && data.challengeId) {
          setChallengeId(data.challengeId);
          setAfterCodePath("/onboarding");
          setLoginStep("code");
          setError("Мы отправили одноразовый код на вашу почту.");
        } else {
          setError(getApiErrorMessage(requestError, "Не удалось зарегистрироваться. Попробуйте еще раз."));
        }
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!login.trim()) {
      setError("Укажите почту или ник.");
      return;
    }
    if (!password) {
      setError("Введите пароль.");
      return;
    }
    if (password.length < 8 || password.length > 30) {
      setError("Пароль должен содержать от 8 до 30 символов.");
      return;
    }

    try {
      setLoading(true);
      const response = await SigninApi.login({
        username: login.trim(),
        password,
        deviceModel: null,
        firebaseId: null,
      });
      finishLogin(response.accessToken, "/app");
    } catch (requestError) {
      const data = getApiErrorData(requestError);
      if (typeof data?.challengeId === "string" && data.challengeId) {
        setChallengeId(data.challengeId);
        setAfterCodePath("/app");
        setLoginStep("code");
        setError("Мы отправили одноразовый код на вашу почту.");
      } else {
        setError(getApiErrorMessage(requestError, "Не удалось войти. Проверьте логин и пароль."));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header variant="public" hideAuthActions />
      <main className="login">
        <section className="login__card">
          <h1 className="login__title">{loginStep === "code" ? "Подтверждение входа" : loginStep === "reset-request" ? "Восстановление пароля" : loginStep === "reset-password" ? "Новый пароль" : mode === "login" ? "Вход" : "Регистрация"}</h1>
          <p className="login__subtitle">
            {loginStep === "code"
              ? "Введите код из письма, чтобы завершить вход."
              : loginStep === "reset-request"
              ? "Укажите email, на который зарегистрирован аккаунт."
              : loginStep === "reset-password"
              ? "Введите код из письма и придумайте новый пароль."
              : mode === "login"
              ? "Войдите по почте или нику и паролю, либо через Яндекс."
              : "Создайте аккаунт по почте и паролю."}
          </p>

          {loginStep === "credentials" ? <div className="login__tabs">
            <button
              className={`login__tab ${mode === "login" ? "login__tab_active" : ""}`}
              type="button"
              onClick={() => switchMode("login")}
            >
              Вход
            </button>
            <button
              className={`login__tab ${mode === "register" ? "login__tab_active" : ""}`}
              type="button"
              onClick={() => switchMode("register")}
            >
              Регистрация
            </button>
          </div> : null}

          <form className="login__form" onSubmit={(event) => void handleSubmit(event)}>
            {loginStep === "code" ? (
              <label className="login__field">
                Одноразовый код
                <input
                  className="login__input"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
                  placeholder="123456"
                />
              </label>
            ) : null}
            {loginStep === "reset-request" ? (
              <label className="login__field">
                Email
                <input className="login__input" autoComplete="email" type="email" value={login} onChange={(event) => setLogin(event.target.value)} placeholder="ivan@example.com" />
              </label>
            ) : null}
            {loginStep === "reset-password" ? (
              <>
                <label className="login__field">
                  Одноразовый код
                  <input className="login__input" inputMode="numeric" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))} placeholder="123456" />
                </label>
                <label className="login__field">
                  Новый пароль
                  <span className="login__passwordField">
                    <input className="login__input" autoComplete="new-password" type={showPassword ? "text" : "password"} value={password} onChange={(event) => setPassword(event.target.value)} placeholder="••••••••" />
                    <button className="login__passwordToggle" type="button" onClick={() => setShowPassword((visible) => !visible)}>{showPassword ? "Скрыть" : "Показать"}</button>
                  </span>
                </label>
                <label className="login__field">
                  Повторите новый пароль
                  <span className="login__passwordField">
                    <input className="login__input" autoComplete="new-password" type={showConfirmPassword ? "text" : "password"} value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} placeholder="••••••••" />
                    <button className="login__passwordToggle" type="button" onClick={() => setShowConfirmPassword((visible) => !visible)}>{showConfirmPassword ? "Скрыть" : "Показать"}</button>
                  </span>
                </label>
              </>
            ) : null}
            {loginStep === "credentials" && mode === "login" ? (
              <label className="login__field">
                Почта или ник
                <input
                  className="login__input"
                  autoComplete="username"
                  value={login}
                  onChange={(event) => setLogin(event.target.value)}
                  placeholder="ivan@yandex.ru или nik"
                />
              </label>
            ) : loginStep === "credentials" ? (
              <label className="login__field">
                Почта
                <input
                  className="login__input"
                  autoComplete="email"
                  type="email"
                  value={mail}
                  onChange={(event) => setMail(event.target.value)}
                  placeholder="ivan@yandex.ru"
                />
              </label>
            ) : null}

            {loginStep === "credentials" ? <label className="login__field">
              Пароль
              <span className="login__passwordField">
                <input
                  className="login__input"
                  autoComplete={mode === "login" ? "current-password" : "new-password"}
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="••••••••"
                />
                <button className="login__passwordToggle" type="button" onClick={() => setShowPassword((visible) => !visible)}>
                  {showPassword ? "Скрыть" : "Показать"}
                </button>
              </span>
            </label> : null}

            {loginStep === "credentials" && mode === "register" ? (
              <label className="login__field">
                Повторите пароль
                <span className="login__passwordField">
                  <input
                    className="login__input"
                    autoComplete="new-password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    placeholder="••••••••"
                  />
                  <button className="login__passwordToggle" type="button" onClick={() => setShowConfirmPassword((visible) => !visible)}>
                    {showConfirmPassword ? "Скрыть" : "Показать"}
                  </button>
                </span>
              </label>
            ) : null}

            {loginStep === "credentials" && mode === "register" ? (
              <div className="login__consents">
                <label className="login__consent">
                  <input
                    type="checkbox"
                    checked={personalDataConsent}
                    onChange={(event) => setPersonalDataConsent(event.target.checked)}
                  />
                  <span>
                    Я даю согласие на обработку персональных данных в соответствии с <a href="/consent" target="_blank" rel="noopener noreferrer">Согласием на обработку персональных данных</a> и <a href="/privacy" target="_blank" rel="noopener noreferrer">Политикой обработки персональных данных</a>, а также принимаю <a href="/offer" target="_blank" rel="noopener noreferrer">Пользовательское соглашение</a>
                  </span>
                </label>
                <label className="login__consent">
                  <input
                    type="checkbox"
                    checked={crossBorderConsent}
                    onChange={(event) => setCrossBorderConsent(event.target.checked)}
                  />
                  <span>
                    Я даю согласие на трансграничную передачу моих персональных данных получателям из США, КНР и иных государств в соответствии с <a href="/cross-border-consent" target="_blank" rel="noopener noreferrer">Согласием на трансграничную передачу персональных данных</a> и <a href="/privacy" target="_blank" rel="noopener noreferrer">Политикой обработки персональных данных</a>
                  </span>
                </label>
              </div>
            ) : null}

            {error ? <p className="login__error">{error}</p> : null}

            <button className="login__submit" type="submit" disabled={loading}>
              {loading ? "Подождите..." : loginStep === "code" ? "Подтвердить код" : loginStep === "reset-request" ? "Отправить код" : loginStep === "reset-password" ? "Сохранить новый пароль" : mode === "login" ? "Войти" : "Зарегистрироваться"}
            </button>
            {loginStep !== "credentials" ? <button className="login__back" type="button" onClick={() => { setLoginStep("credentials"); setCode(""); setError(null); }}>Вернуться ко входу</button> : null}
          </form>

          {mode === "login" && loginStep === "credentials" ? <button className="login__forgot" type="button" onClick={() => { setLoginStep("reset-request"); setError(null); setPassword(""); }}>Забыли пароль?</button> : null}

          {loginStep === "credentials" ? <div className="login__divider">
            <span>или</span>
          </div> : null}

          {loginStep === "credentials" ? <YandexLoginButton className="login__yandex" /> : null}
        </section>
      </main>
    </>
  );
};
